import React, { useState, useEffect } from 'react'
import { FormattedMessage } from 'react-intl'
import { PlusCircle, Save2, Trash, PersonLinesFill, CardHeading, CreditCard2Front, GeoAlt, EnvelopeAt, Telephone, CardText, GlobeAmericas, Geo, Map, CardList, MapFill } from 'react-bootstrap-icons'
import { Button, Form, Row, Col } from 'react-bootstrap'

import EntitiesService from '../services/EntitiesService'
import { AlertMsg, AlertError, AlertLoading } from '../helpers/AlertHelper'
import entitiesConfig from '../config/entities'

const EntitiesForm = (props) => {

    const [elementId, setElementId] = useState(props.elementId)
    const [action, setAction] = useState(props.action)
    const [type, setType] = useState(entitiesConfig.DEFAULT_TYPE)
    const [name, setName] = useState('')
    const [vat, setVat] = useState('')
    const [address, setAddress] = useState('')
    const [zipCode, setZipCode] = useState('')
    const [city, setCity] = useState('')
    const [province, setProvince] = useState('')
    const [country, setCountry] = useState('')
    const [phone, setPhone] = useState('')
    const [email, setEmail] = useState('')
    const [emailErrorKey, setEmailErrorKey] = useState('');
    const [description, setDescription] = useState('')
    const [buttonTitle, setButtonTitle] = useState('')
    const [inputDisabled, setInputDisabled] = useState(false)
    const [formValid, setFormValid] = useState(false)

    const [showMsg, setShowMsg] = useState(false)
    const [showAlert, setShowAlert] = useState(false)
    const [isLoaded, setIsLoaded] = useState(false)
    const [error, setError] = useState(false)

    const getData = async () => {
        await EntitiesService.getEntity(elementId).then(
            response => {
                setType(response.data.type || entitiesConfig.DEFAULT_TYPE)
                setName(response.data.name || '')
                setVat(response.data.vat || '')
                setAddress(response.data.address || '')
                setZipCode(response.data.zipCode || '')
                setCity(response.data.city || '')
                setProvince(response.data.province || '')
                setCountry(response.data.country || '')
                setPhone(response.data.phone || '')
                setEmail(response.data.email || '')
                setDescription(response.data.description || '')
                setIsLoaded(true)
            },
            error => {
                setError(error)
            })
    }

    useEffect(() => {

        if (elementId) {
            getData()
        }

        switch (action) {
            case 'edit':
                setButtonTitle(<FormattedMessage id="textSave" defaultMessage="Save" />)
                break
            case 'delete':
                setButtonTitle(<FormattedMessage id="textDelete" defaultMessage="Delete" />)
                setInputDisabled(true)
                break
            default:
                setButtonTitle(<FormattedMessage id="textAdd" defaultMessage="Add" />)
        }

    }, [])

    const onSubmit = () => {
        props.onSubmit()
    }

    const handleInputChange = (e) => {
        const name = e.target.name
        const value = e.target.value
        if (name === 'email') {
            setEmailErrorKey(''); 
        }

        setFieldValue(name, value)
    }

    const setFieldValue = (fieldName, value) => {
        switch (fieldName) {
            case 'type':
                setType(value)
                break
            case 'name':
                setName(value)
                break
            case 'vat':
                setVat(value)
                break
            case 'address':
                setAddress(value)
                break
            case 'zipCode':
                setZipCode(value)
                break
            case 'city':
                setCity(value)
                break
            case 'province':
                setProvince(value)
                break
            case 'country':
                setCountry(value)
                break
            case 'phone':
                setPhone(value)
                break
            case 'email':
                setEmail(value)
                break
            case 'description':
                setDescription(value)
                break
        }
    }

    const handleSubmit = async (e) => {
        e.preventDefault()
        e.stopPropagation()

        const form = e.currentTarget
        if (!form.checkValidity()) {
            setFormValid(true)
            return
        }

        const entity = {
            type,
            name,
            vat,
            address,
            zipCode,
            city,
            province,
            country,
            phone,
            email,
            description,
        }

        try {
            if (elementId && action === "edit") {
                await EntitiesService.updateEntity(entity, elementId)
            } else if (action === "add") {
                await EntitiesService.addEntity(entity)
            } else if (action === "delete") {
                await EntitiesService.deleteEntity(elementId)
            }

            setFormValid(false)
            onSubmit()
        } catch (error) {
            const serverError = error.response?.data;
            
            if (serverError?.errorKey === "entitiesEmailAlreadyExists") {
                setEmailErrorKey(serverError.errorKey);
                setFormValid(true);
            } else {
                setShowMsg({ text: error.response?.data?.msg || "An error occurred", style: 'danger' })
                setShowAlert(true)
            }
        } 
    }

    if (error) {
        return (
            <AlertError error={error} />
        )
    } else if (elementId && !isLoaded) {
        return (
            <AlertLoading isLoaded={isLoaded} />
        )
    }

    return (
        <div>
            <Form noValidate validated={formValid} onSubmit={handleSubmit}>
                {elementId && (
                    <Form.Group className="mb-3" controlId="id">
                        <Form.Label className='fw-semibold'>Id:</Form.Label>
                        <Form.Control
                            type="text"
                            name="id"
                            value={elementId}
                            disabled={"disabled"}
                        />
                    </Form.Group>
                )}

                <Form.Group className="mb-3" controlId="type">
                    <CardList /><Form.Label className="fw-semibold mb-2 ms-2"><FormattedMessage id="entityType" defaultMessage="Type" />:</Form.Label>
                    <Form.Control
                        as="select"
                        name="type"
                        value={type}
                        onChange={(e) => {
                            handleInputChange({ target: { name: e.target.name, value: e.target.value } });
                        }}
                        disabled={inputDisabled && ("disabled")}
                        required
                    >
                        <option key='blank_type' value=''><FormattedMessage id="textSelect" defaultMessage="Select" /> ...</option>
                        {entitiesConfig.ENTITY_TYPES.map((entityType) => (
                            <option key={entityType} value={entityType}>
                                {entityType}
                            </option>
                        ))}
                    </Form.Control>
                    <Form.Control.Feedback type="invalid">
                        <FormattedMessage id="entitiesTypeInvalid" defaultMessage="Select an entity type" />
                    </Form.Control.Feedback>
                </Form.Group>

                <Row className="mt-4 mb-4">
                    <Col md={6}>
                        <Form.Group className="mb-3" controlId="name">
                            <PersonLinesFill /><Form.Label className="fw-semibold mb-2 ms-2"><FormattedMessage id="entitiesName" defaultMessage="Name" />:</Form.Label>
                            <Form.Control
                                type="text"
                                name="name"
                                value={name}
                                onChange={handleInputChange}
                                disabled={inputDisabled && ("disabled")}
                                required="required"
                            />
                            <Form.Control.Feedback type="invalid">
                                <FormattedMessage id="entitiesNameInvalid" defaultMessage="Provide an entity name" />
                            </Form.Control.Feedback>
                        </Form.Group>
                    </Col>
                    <Col md={6}>
                        <Form.Group className="mb-3" controlId="vat">
                            <CardHeading /><Form.Label className="fw-semibold mb-2 ms-2"><FormattedMessage id="entitiesVAT" defaultMessage="VAT number" />:</Form.Label>
                            <Form.Control
                                type="text"
                                name="vat"
                                value={vat}
                                onChange={handleInputChange}
                                disabled={inputDisabled && ("disabled")}
                                required="required"
                            />
                            <Form.Control.Feedback type="invalid">
                                <FormattedMessage id="entitiesVATInvalid" defaultMessage="Provide a valid VAT number" />
                            </Form.Control.Feedback>
                        </Form.Group>
                    </Col>
                </Row>

                <Form.Group className="mb-3" controlId="address">
                    <GeoAlt /><Form.Label className="fw-semibold mb-2 ms-2"><FormattedMessage id="entitiesAddress" defaultMessage="Address" />:</Form.Label>
                    <Form.Control
                        type="text"
                        name="address"
                        value={address}
                        onChange={handleInputChange}
                        disabled={inputDisabled && ("disabled")}
                    />
                    <Form.Control.Feedback type="invalid">
                        <FormattedMessage id="entitiesAddressInvalid" defaultMessage="Provide an address" />
                    </Form.Control.Feedback>
                </Form.Group>

                <Row className="mt-4 mb-4">
                    <Col md={6}>
                        <Form.Group className="mb-3" controlId="zipCode">
                            <Geo /><Form.Label className="fw-semibold mb-2 ms-2"><FormattedMessage id="entitiesZipCode" defaultMessage="ZIP Code" />:</Form.Label>
                            <Form.Control
                                type="text"
                                name="zipCode"
                                value={zipCode}
                                onChange={handleInputChange}
                                disabled={inputDisabled && ("disabled")}
                            />
                            <Form.Control.Feedback type="invalid">
                                <FormattedMessage id="entitiesZipCodeInvalid" defaultMessage="Provide a ZIP code" />
                            </Form.Control.Feedback>
                        </Form.Group>
                    </Col>
                    <Col md={6}>
                        <Form.Group className="mb-3" controlId="city">
                            <Map /><Form.Label className="fw-semibold mb-2 ms-2"><FormattedMessage id="entitiesCity" defaultMessage="City" />:</Form.Label>
                            <Form.Control
                                type="text"
                                name="city"
                                value={city}
                                onChange={handleInputChange}
                                disabled={inputDisabled && ("disabled")}
                            />
                            <Form.Control.Feedback type="invalid">
                                <FormattedMessage id="entitiesCityInvalid" defaultMessage="Provide a city" />
                            </Form.Control.Feedback>
                        </Form.Group>
                    </Col>
                </Row>

                <Row className="mt-4 mb-4">
                    <Col md={6}>
                        <Form.Group className="mb-3" controlId="province">
                            <MapFill /><Form.Label className="fw-semibold mb-2 ms-2"><FormattedMessage id="entitiesProvince" defaultMessage="Province" />:</Form.Label>
                            <Form.Control
                                type="text"
                                name="province"
                                value={province}
                                onChange={handleInputChange}
                                disabled={inputDisabled && ("disabled")}
                            />
                            <Form.Control.Feedback type="invalid">
                                <FormattedMessage id="entitiesProvinceInvalid" defaultMessage="Provide a province" />
                            </Form.Control.Feedback>
                        </Form.Group>
                    </Col>
                    <Col md={6}>
                        <Form.Group className="mb-3" controlId="country">
                            <GlobeAmericas /><Form.Label className="fw-semibold mb-2 ms-2"><FormattedMessage id="entitiesCountry" defaultMessage="Country" />:</Form.Label>
                            <Form.Control
                                type="text"
                                name="country"
                                value={country}
                                onChange={handleInputChange}
                                disabled={inputDisabled && ("disabled")}
                            />
                            <Form.Control.Feedback type="invalid">
                                <FormattedMessage id="entitiesCountryInvalid" defaultMessage="Provide a country" />
                            </Form.Control.Feedback>
                        </Form.Group>
                    </Col>
                </Row>

                <Row className="mt-4 mb-4">
                    <Col md={6}>
                        <Form.Group className="mb-3" controlId="phone">
                            <Telephone /><Form.Label className="fw-semibold mb-2 ms-2"><FormattedMessage id="entitiesPhoneNumber" defaultMessage="Phone number" />:</Form.Label>
                            <Form.Control
                                type="text"
                                name="phone"
                                value={phone}
                                onChange={handleInputChange}
                                disabled={inputDisabled && ("disabled")}
                            />
                            <Form.Control.Feedback type="invalid">
                                <FormattedMessage id="entitiesPhoneNumberInvalid" defaultMessage="Provide a phone number" />
                            </Form.Control.Feedback>
                        </Form.Group>
                    </Col>
                    <Col md={6}>
                        <Form.Group className="mb-3" controlId="email">
                            <EnvelopeAt /><Form.Label className="fw-semibold mb-2 ms-2"><FormattedMessage id="entitiesEmail" defaultMessage="E-mail" />:</Form.Label>
                            <Form.Control
                                type="email"
                                name="email"
                                value={email}
                                onChange={handleInputChange}
                                disabled={inputDisabled && ("disabled")}
                                className={emailErrorKey !== '' ? 'is-invalid border-danger' : (formValid ? 'is-valid' : '')}
                                pattern="^([a-zA-Z0-9_\-\.]+)@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.)|(([a-zA-Z0-9\-]+\.)+))([a-zA-Z]{2,4}|[0-9]{1,3})(\]?)$"
                                style={emailErrorKey !== '' ? { backgroundImage: 'none' } : {}}
                            />
                           {emailErrorKey !== '' ? (
                                <div className="text-danger small mt-1" style={{ display: 'block', fontWeight: 'normal' }}>
                                    <FormattedMessage id={emailErrorKey} />
                                </div>
                            ) : (
                                <Form.Control.Feedback type="invalid">
                                    <FormattedMessage id="entitiesEmailInvalid" defaultMessage="Provide a valid Email address" />
                                </Form.Control.Feedback>
                            )}
                        </Form.Group>
                    </Col>
                </Row>

                <Form.Group className="mb-3" controlId="description">
                    <CardText /><Form.Label className="fw-semibold mb-2 ms-2"><FormattedMessage id="entitiesDescription" defaultMessage="Description" />:</Form.Label>
                    <Form.Control
                        name="description"
                        value={description}
                        as="textarea"
                        disabled={inputDisabled && ("disabled")}
                        rows={3}
                        onChange={handleInputChange}
                    />
                </Form.Group>

                <AlertMsg message={showMsg.text} variant={showMsg.style} dismissible onCloseHandler={() => setShowAlert(false)} show={showAlert} />

                <div className="d-grid gap-2">
                    {action === "add" && (
                        <Button
                            className="btn btn-dark fw-semibold"
                            type="submit"
                        >
                            <PlusCircle /> {buttonTitle}
                        </Button>
                    )}

                    {action === "edit" && (
                        <Button
                            className="btn btn-primary fw-semibold"
                            type="submit"
                        >
                            <Save2 /> {buttonTitle}
                        </Button>
                    )}

                    {action === "delete" && (
                        <Button
                            className="btn btn-danger fw-semibold"
                            type="submit"
                        >
                            <Trash /> {buttonTitle}
                        </Button>
                    )}
                </div>
            </Form>
        </div>
    )

}

export default EntitiesForm